import startsWith from 'lodash/startsWith';
import { SeverityGroups } from '@common/constants/eventSeverity';
import { createCountryKey, createValueDefinitionKey } from '@common/i18n/createKey';
import { type InstallationsInfoQuery } from '@common/types/api';
import {
  type CurrentFilterData,
  type FilterSuggestion,
  type FormFilterData,
} from '@common/types/deviceFilters';
import nonExistentValue from '@common/utils/constants';

export interface FilterConfig {
  onlines: boolean | undefined;
  offlines: boolean | undefined;
  hasLastOnlineUtc: boolean | undefined;
  eventSeverity?: InstallationsInfoQuery['eventSeverity'];
  hasPendingRetailerAccess?: boolean;
  hasPendingDeviceDeletion?: boolean;
  hasDeviceId?: boolean;
}

export const ConfigOnline: FilterConfig = {
  onlines: true,
  offlines: false,
  hasLastOnlineUtc: undefined,
};

export const ConfigAllOk: FilterConfig = {
  onlines: undefined,
  offlines: undefined,
  hasLastOnlineUtc: undefined,
  eventSeverity: [...SeverityGroups.ALL_OK],
};

export const ConfigRegularError: FilterConfig = {
  onlines: undefined,
  offlines: undefined,
  hasLastOnlineUtc: undefined,
  eventSeverity: [...SeverityGroups.REGULAR_ERROR],
};

export const ConfigCriticalError: FilterConfig = {
  onlines: undefined,
  offlines: undefined,
  hasLastOnlineUtc: undefined,
  eventSeverity: [...SeverityGroups.CRITICAL_ERROR],
};

export const ConfigOffline: FilterConfig = {
  onlines: false,
  offlines: true,
  hasLastOnlineUtc: true,
};

export const ConfigNeverConnected: FilterConfig = {
  onlines: undefined,
  offlines: undefined,
  hasLastOnlineUtc: false,
};

export const ConfigAllDevices: FilterConfig = {
  onlines: true,
  offlines: true,
  hasLastOnlineUtc: undefined,
};

export const ConfigAccessRequested: FilterConfig = {
  onlines: undefined,
  offlines: undefined,
  hasLastOnlineUtc: undefined,
  hasPendingRetailerAccess: true,
};

export const ConfigDeleted: FilterConfig = {
  onlines: undefined,
  offlines: undefined,
  hasLastOnlineUtc: undefined,
  hasPendingDeviceDeletion: true,
};

export const ConfigPaired: FilterConfig = {
  onlines: undefined,
  offlines: undefined,
  hasLastOnlineUtc: undefined,
  hasDeviceId: true,
};

export const ConfigNeverPaired: FilterConfig = {
  onlines: undefined,
  offlines: undefined,
  hasLastOnlineUtc: undefined,
  hasDeviceId: false,
};

const resolveConfigField = (
  field1?: boolean,
  field2?: boolean,
): boolean | undefined => {
  if (field1 === undefined && field2 === undefined) {
    return undefined;
  }
  return !!field1 || !!field2;
};

const sumPairOfConfigs = (one: FilterConfig, another: FilterConfig): FilterConfig => {
  const calculatedConfig: FilterConfig = {
    onlines: resolveConfigField(one.onlines, another.onlines),
    offlines: resolveConfigField(one.offlines, another.offlines),
    hasLastOnlineUtc: resolveConfigField(one.hasLastOnlineUtc, another.hasLastOnlineUtc),
    hasPendingRetailerAccess: resolveConfigField(
      one.hasPendingRetailerAccess,
      another.hasPendingRetailerAccess,
    ),
    hasPendingDeviceDeletion: resolveConfigField(
      one.hasPendingDeviceDeletion,
      another.hasPendingDeviceDeletion,
    ),
    // undefined also means fetch both
    hasDeviceId: one.hasDeviceId === another.hasDeviceId ? one.hasDeviceId : undefined,
  };

  if (one.eventSeverity || another.eventSeverity) {
    calculatedConfig.eventSeverity = [
      ...(one.eventSeverity || []),
      ...(another.eventSeverity || []),
    ];
  }

  if (!calculatedConfig.offlines && !calculatedConfig.onlines) {
    calculatedConfig.offlines = undefined!;
    calculatedConfig.onlines = undefined!;
  }
  return calculatedConfig;
};

export default function calculateFilters(
  ...args: FilterConfig[]
): FilterConfig {
  const [firstArg, secondArg, ...restArgs] = args;
  if (!firstArg) {
    return ConfigAllDevices;
  }
  if (!secondArg) {
    return firstArg;
  }
  const accumulation = sumPairOfConfigs(firstArg, secondArg);
  if (args.length > 0) {
    const [nextArg, ...rest] = restArgs;
    return calculateFilters(accumulation, nextArg, ...rest);
  }
  return accumulation;
}

export enum FieldName {
  postalCodeFilterFieldName = 'selectedPostalCodes',
  cityFilterFieldName = 'selectedCities',
  countryFilterFieldName = 'selectedCountries',
  controllerUnitFieldName = 'selectedControllerUnits',
  extensionFieldName = 'selectedExtensions',
  firmwareFieldName = 'selectedFirmware',
  communicationFirmwareFieldName = 'selectedCommunicationFirmware',
  deviceTypeFieldName = 'selectedDeviceTypes',
  companyFilterFieldName = 'selectedCompanies',
  contractTypeFieldName = 'selectedContract',
}

interface FilterData {
  prefix: string;
  formField?: {
    fieldName: string;
    // Field value doesn't include filter name prefix.
    // Currently true for input and dropdown filters that use a single value.
    noFilterPrefix?: true;
  };
}

export enum FilterName {
  city = 'city',
  communicationFirmware = 'communicationFirmware',
  company = 'company',
  contractType = 'contractType',
  controllerUnit = 'controllerUnit',
  country = 'country',
  deviceType = 'deviceType',
  extension = 'extension',
  externalFilter = 'externalFilter',
  firmware = 'firmware',
  operationMode = 'operationMode',
  postalCode = 'postalCode',
  role = 'role',
  userStatus = 'userStatus',
  email = 'email',
}

export enum FilterPrefix {
  city = 'city-',
  communicationFirmware = 'cfw-',
  company = 'company-',
  contractType = 'contractTypeId-',
  controllerUnit = 'cu-',
  country = 'country-',
  deviceType = 'dt-',
  extension = 'ext-',
  externalFilter = 'external-',
  firmware = 'fw-',
  operationMode = 'om-',
  postalCode = 'postalCode-',
  role = 'role-',
  userStatus = 'st-',
  email = 'email-',
  status = 'status-',
}

export type FilterParameterName = `${FilterPrefix}${string}`;

type FilterDataType = { [key in keyof typeof FilterName]: FilterData; };

export const valueDefinitionTypes: Partial<Record<FilterName, string>> = {
  deviceType: 'DeviceType',
  controllerUnit: 'ControllerUnit',
  extension: 'DeviceExtension',
  operationMode: 'DeviceOperationMode',
};

export const allFilters: FilterDataType = {
  deviceType: {
    prefix: FilterPrefix.deviceType,
    formField: {
      fieldName: FieldName.deviceTypeFieldName,
    },
  },
  contractType: {
    prefix: FilterPrefix.contractType,
    formField: {
      fieldName: FieldName.contractTypeFieldName,
      noFilterPrefix: true,
    },
  },
  postalCode: {
    prefix: FilterPrefix.postalCode,
    formField: {
      fieldName: FieldName.postalCodeFilterFieldName,
      noFilterPrefix: true,
    },
  },
  city: {
    prefix: FilterPrefix.city,
    formField: {
      fieldName: FieldName.cityFilterFieldName,
    },
  },
  country: {
    prefix: FilterPrefix.country,
    formField: {
      fieldName: FieldName.countryFilterFieldName,
    },
  },
  controllerUnit: {
    prefix: FilterPrefix.controllerUnit,
    formField: {
      fieldName: FieldName.controllerUnitFieldName,
    },
  },
  extension: {
    prefix: FilterPrefix.extension,
    formField: {
      fieldName: FieldName.extensionFieldName,
    },
  },
  firmware: {
    prefix: FilterPrefix.firmware,
    formField: {
      fieldName: FieldName.firmwareFieldName,
    },
  },
  communicationFirmware: {
    prefix: FilterPrefix.communicationFirmware,
    formField: {
      fieldName: FieldName.communicationFirmwareFieldName,
    },
  },
  operationMode: {
    prefix: FilterPrefix.operationMode,
  },
  role: {
    prefix: FilterPrefix.role,
  },
  company: {
    prefix: FilterPrefix.company,
    formField: {
      fieldName: FieldName.companyFilterFieldName,
    },
  },
  userStatus: {
    prefix: FilterPrefix.userStatus,
  },
  email: {
    prefix: FilterPrefix.email,
  },
  externalFilter: {
    prefix: FilterPrefix.externalFilter,
  },
};

export const findFilterName = (
  key: string,
) =>
  Object.keys(allFilters)
    .find((name) => key.startsWith(allFilters[name as FilterName].prefix)) as FilterName;

export const translationKeyForValueDefinition = (filterName: FilterName, value: string) => {
  switch (filterName) {
    case FilterName.controllerUnit:
    case FilterName.deviceType:
    case FilterName.extension:
      return createValueDefinitionKey(value);

    default:
      return value;
  }
};

const translationKeyForFilterValue = (filterName: FilterName, value: string) => {
  switch (filterName) {
    case FilterName.country:
      return createCountryKey(value);

    case FilterName.controllerUnit:
    case FilterName.deviceType:
    case FilterName.extension:
      return createValueDefinitionKey(value);

    default:
      return value;
  }
};

export const createOptionValue = (
  filterName: FilterName,
  s: string,
): string => `${allFilters[filterName].prefix}${s}`;

export const extractKeyNumber = (
  s: string,
  filterName: FilterName,
): number => Number(s.slice(allFilters[filterName].prefix.length));

export const extractKeyText = (
  s: string,
  filterName: FilterName,
) => s.slice(allFilters[filterName].prefix.length);

export const extractKeyTextAsTranslationKey = (
  s: string,
  filterName: FilterName,
): string => {
  const text = extractKeyText(s, filterName);
  return translationKeyForFilterValue(filterName, text);
};

export const extractKeyTextValueFromDataSource = (
  s: string,
  filterName: FilterName,
  dataSource: Array<FilterSuggestion>,
): string => {
  const text = extractKeyText(s, filterName);
  const optionForValue = dataSource.find((option) => option.id === text);
  return optionForValue?.value ?? text;
};

export const isPrefixedWith = (s: string, filterName: FilterName): FilterName | undefined => {
  if (startsWith(s, allFilters[filterName].prefix)) {
    return filterName;
  }
  return undefined;
};

/**
 * Return filter keys from filterData for a single filterName.
 * @param filterData
 * @param filterName
 */
const getFormFieldFilters = (
  filterData: CurrentFilterData,
  filterName: FilterName,
): string[] =>
  Object.keys(filterData)
    .filter((key) =>
      filterData[key as FilterParameterName]
      && startsWith(key, allFilters[filterName].prefix)
    );

/**
 * Return true if fieldName belongs to Input or ComboboxInput.
 * Example fieldname: 'selectedCities'.
 * @param fieldName
 */
const isFormFieldName = (
  fieldName: string,
) =>
  Object.values(allFilters)
    .filter((filterData) => filterData.formField)
    .some((filterData) => filterData.formField!.fieldName === fieldName);

/**
 * Return true if the key has a prefix that belongs to Input or ComboboxInput filter.
 * Example key: 'city-Tallinn'.
 * @param key
 */
const isFormFieldFilterPrefix = (
  key: string,
) =>
  (Object.keys(allFilters) as FilterName[])
    .map((filterName) => allFilters[filterName])
    .filter((filterData) => filterData.formField)
    .some((filterData) => startsWith(key, filterData.prefix));

/**
 * Return all filters in an object ready to be supplied to Formik.
 * Input and ComboboxInput filter values have each a separate sub-array of keys; it is implied
 * that the values for those are always true. The rest are added as regular array elements.
 * Example result: {"status-Online":true,"selectedCities":["city-Tallinn"]}
 * @param filterData
 */
export const buildFormData = (filterData: CurrentFilterData): FormFilterData => {
  const result = Object.keys(filterData).reduce<FormFilterData>((accumulator, key) => {
    const filterValue = filterData[key as FilterParameterName];
    // We don't set falsy values so check filterValue!
    if (filterValue && !(isFormFieldName(key) || isFormFieldFilterPrefix(key))) {
      return {
        ...accumulator,
        [key]: filterValue,
      };
    }
    return accumulator;
  }, {});

  (Object.keys(allFilters) as FilterName[])
    .filter((filterName) => allFilters[filterName].formField)
    .forEach((filterName) => {
      const filters = getFormFieldFilters(filterData, filterName);
      if (filters.length > 0) {
        const { fieldName, noFilterPrefix } = allFilters[filterName].formField!;
        if (fieldName) {
          let updatedFilters = filters;
          if (noFilterPrefix) {
            updatedFilters = filters
              .map((filter) => extractKeyText(filter, filterName));
          }
          result[fieldName as FieldName] = updatedFilters;
        }
      }
    });
  return result;
};

/**
 * Return all filters in formFilterData as an array of key-value pairs where value
 * describes whether the filter is active or not.
 * Process chips and input field entries separately.
 * Example result: {"status-Online":true,"city-Tallinn":true}
 * @param formFilterData
 */
export const buildFilterData = (formFilterData: FormFilterData): CurrentFilterData => {
  const result = Object.keys(formFilterData)
    .reduce<CurrentFilterData>(
      (accumulator, key) => {
        const filterValue = formFilterData[key as FieldName];
        // We don't set falsy values so check filterValue!
        if (filterValue && !(Array.isArray(filterValue))) {
          return {
            ...accumulator,
            [key]: filterValue,
          };
        }
        return accumulator;
      },
      {},
    );

  Object.values(allFilters)
    .filter((filterData) => filterData.formField)
    .forEach(({ prefix, formField }) => {
      const { fieldName, noFilterPrefix } = formField!;
      const filters = formFilterData[fieldName as FieldName];
      if (Array.isArray(filters)) {
        filters.map((filter) => filter.trim())
          .filter((filter) => filter && filter !== nonExistentValue)
          .forEach((filter) => {
            let updatedFilter = filter;
            if (noFilterPrefix) {
              updatedFilter = `${prefix}${filter}`;
            }
            result[updatedFilter as FilterParameterName] = true;
          });
      }
    });
  return result;
};
